import { message } from "antd"
import axios, { AxiosHeaders, AxiosRequestHeaders } from "axios"
import { onHttpRequestError } from "./onHttpRequestError"

axios.defaults.baseURL = process.env.NEXT_PUBLIC_API_URL

export const getAdminToken = () => {
  try {
    const token = localStorage.getItem("auth_token")
    return token ? `Bearer ${token}` : null
  } catch (ex) {
    return null
  }
}

const handleTokenExpired = () => {
  try {
    localStorage.removeItem("auth_token")
    localStorage.removeItem("user")
    localStorage.removeItem("permissions")

    // Mirror clear into admin cookies so middleware gate resets too.
    if (typeof document !== "undefined") {
      document.cookie = "admin_auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT"
      document.cookie = "admin_user_meta=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT"
      document.cookie = "admin_hint=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT"
    }

    message.error("Hết hạn đăng nhập, vui lòng đăng nhập lại")

    if (typeof window !== "undefined") {
      window.location.href = "/login"
    }
  } catch (error) {
    console.error("Error during logout:", error)
  }
}

const adminAxios = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL
})

/**
 * Request interceptor: auto-inject the current admin site's domain filter
 * into every Strapi request so the admin only ever sees data for the site
 * the user picked. Each request can opt out via `skipDomainFilter: true`
 * in its config (e.g. for super-admin global lists).
 */
adminAxios.interceptors.request.use((config: any) => {
  if (typeof window === "undefined") return config
  if (config.skipDomainFilter) return config
  try {
    // Lazy import to avoid circular deps (store → utils → http client).
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { useDomainStore } = require("@/stores/use-domain-store")
    const selected = useDomainStore.getState().selectedDomain
    const domain = selected?.attributes?.domain
    if (domain && selected.id !== 0) {
      config.params = config.params || {}
      // Don't overwrite an explicit filter the caller already set.
      if (!config.params["filters[domain][$eq]"] && !config.params?.filters?.domain) {
        config.params["filters[domain][$eq]"] = domain
      }
    }
  } catch {
    /* ignore — never block a request over interceptor errors */
  }
  return config
})

adminAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      handleTokenExpired()
    }
    return Promise.reject(error)
  }
)

const withAdminAuth = (headers: AxiosHeaders = new AxiosHeaders()): AxiosHeaders => {
  const token = getAdminToken()
  if (token) {
    headers.set("Authorization", token)
  }
  return headers
}

const catchTransactionError = async (asyncFunc: any, apiName = "") => {
  try {
    const response = await asyncFunc
    return response ? response.data : null
  } catch (error: any) {
    if (error.response && error.response.status === 401) {
      return null
    }
    
    let response = {
      data: { data: null, error: null },
      message: error.message,
    }
    if (error.response) {
      response = { ...error.response, message: error.message }
    }
    onHttpRequestError(response, apiName)
  }
}

interface ConfigsProps {
  headers?: AxiosRequestHeaders
  apiName?: string
  params?: object
  data?: object
  /**
   * Opt out of the automatic `filters[domain][$eq]=<current-site>` injection
   * for a single request. Use for global lists (e.g. super-admin user index,
   * cross-site reports) or when the endpoint doesn't support the domain field.
   */
  skipDomainFilter?: boolean
}

export const getAdminRequest = async <T = any>(url: string, configs: ConfigsProps = {}): Promise<T> =>
  catchTransactionError(
    adminAxios.get(url, {
      headers: withAdminAuth(new AxiosHeaders(configs.headers)),
      ...configs,
    }),
    configs.apiName,
  )

export const postAdminRequest = async (url: string, data: any = null, configs: ConfigsProps = {}) =>
  catchTransactionError(
    adminAxios.post(url, data, {
      headers: withAdminAuth(new AxiosHeaders(configs.headers)),
      ...configs,
    }),
    configs.apiName,
  )

export const putAdminRequest = async (url: string, data: any, configs: ConfigsProps = {}) =>
  catchTransactionError(
    adminAxios.put(url, data, {
      headers: withAdminAuth(new AxiosHeaders(configs.headers)),
      ...configs,
    }),
    configs.apiName,
  )

export const patchAdminRequest = async (url: string, data: any, configs: ConfigsProps = {}) =>
  catchTransactionError(
    adminAxios.patch(url, data, {
      headers: withAdminAuth(new AxiosHeaders(configs.headers)),
      ...configs,
    }),
    configs.apiName,
  )

export const deleteAdminRequest = async (url: string, configs: ConfigsProps = {}) =>
  catchTransactionError(
    adminAxios.delete(url, {
      headers: withAdminAuth(new AxiosHeaders(configs.headers)),
      ...configs,
    }),
    configs.apiName,
  )

export class AdminHttpClient {
  static get = getAdminRequest
  static post = postAdminRequest
  static put = putAdminRequest
  static patch = patchAdminRequest
  static delete = deleteAdminRequest
}